import React, { useEffect, useRef, useState } from 'react';

// cLabels is stored as a plain comma-separated string of label ids,
// e.g. "3,7,12" — this turns that into a Set of numbers for cheap
// membership checks, and safely handles null/empty/malformed values.
export function parseLabelIds(cLabels) {
  if (!cLabels) return [];
  return String(cLabels)
    .split(',')
    .map(s => parseInt(s.trim(), 10))
    .filter(n => Number.isFinite(n));
}

export function labelsForConversation(conv, labelsById) {
  return parseLabelIds(conv?.cLabels).map(id => labelsById[id]).filter(Boolean);
}

// NOTE ON STYLING APPROACH — every element below sets its box-model and
// visual properties (width, height, border-radius, padding, background,
// display…) via the inline `style` prop rather than a CSS className.
// The host dashboard's own stylesheet was overriding/stripping our
// classNames (confirmed: a swatch's inline `background` rendered fine,
// but its className-driven width/height/border-radius collapsed to 0),
// so nothing here can safely depend on an external stylesheet applying.
// Inline styles have higher specificity than the host's un-!important
// rules, so this is deliberately verbose but reliable everywhere.

// ── Small colored pill ───────────────────────────────────────
export function LabelChip({ label, onRemove, small }) {
  const [hover, setHover] = useState(false);
  if (!label) return null;
  const pad = small ? '1px 7px' : '2px 9px';
  const fontSize = small ? 10 : 11;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: pad,
        borderRadius: 999,
        border: `1.5px solid ${label.lColor}55`,
        background: `${label.lColor}22`,
        color: label.lColor,
        fontSize,
        fontWeight: 700,
        lineHeight: 1.6,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
      }}
    >
      {label.lName}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 12,
            height: 12,
            marginLeft: 2,
            padding: 0,
            border: 'none',
            borderRadius: '50%',
            background: 'transparent',
            color: 'inherit',
            fontSize: 9,
            lineHeight: 1,
            cursor: 'pointer',
            opacity: hover ? 1 : 0.65,
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            appearance: 'none',
          }}
        >
          ✕
        </button>
      )}
    </span>
  );
}

// ── Circular color swatch (used by the label create/edit modal) ──
export function ColorSwatch({ color, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={color}
      style={{
        display: 'inline-block',
        width: 26,
        height: 26,
        minWidth: 26,
        minHeight: 26,
        flexShrink: 0,
        borderRadius: '50%',
        border: active ? '2px solid #111827' : '2px solid transparent',
        boxShadow: active ? '0 0 0 2px #fff inset' : 'none',
        background: color,
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        boxSizing: 'border-box',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        appearance: 'none',
        outline: 'none',
      }}
    />
  );
}

// ── Toggle popover — checkbox list of every label, used from the
//    chat header's "+ Label" button to assign/unassign in place ──
export function LabelPickerPopover({ labels, selectedIds, onToggle, onClose }) {
  const ref = useRef(null);
  const [hoverId, setHoverId] = useState(null);

  useEffect(() => {
    function onDocClick(e) { if (ref.current && !ref.current.contains(e.target)) onClose(); }
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        marginTop: 4,
        minWidth: 190,
        maxWidth: 260,
        background: '#fff',
        border: '1.5px solid #e5e7eb',
        borderRadius: 10,
        boxShadow: '0 6px 20px rgba(0,0,0,0.16)',
        zIndex: 50,
        overflow: 'hidden',
      }}
    >
      {labels.length === 0 ? (
        <div style={{ padding: '10px 12px', fontSize: 11.5, color: '#6b7280' }}>
          No labels yet — create one in the Labels tab.
        </div>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 4, maxHeight: 240, overflowY: 'auto' }}>
          {labels.map(l => {
            const checked = selectedIds.includes(l.lId);
            const hovered = hoverId === l.lId;
            return (
              <li
                key={l.lId}
                onClick={() => onToggle(l.lId)}
                onMouseEnter={() => setHoverId(l.lId)}
                onMouseLeave={() => setHoverId(null)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '7px 8px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#1f2937',
                  background: hovered ? '#f3f4f6' : 'transparent',
                }}
              >
                <span style={{
                  display: 'inline-block',
                  width: 9,
                  height: 9,
                  minWidth: 9,
                  borderRadius: '50%',
                  background: l.lColor,
                  flexShrink: 0,
                }} />
                <span style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {l.lName}
                </span>
                <span style={{
                  width: 14,
                  flexShrink: 0,
                  color: '#00C88C',
                  fontWeight: 900,
                  fontSize: 12,
                  textAlign: 'center',
                }}>
                  {checked ? '✓' : ''}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}